import z from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import {
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Box,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ItemCircuitData, itemCircuitDataDto, stringifyPowerType } from '@nohara-dxdev/mt-common';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { FormProvider, useForm, useFormContext, useFormState } from 'react-hook-form';
import { TextFieldElement } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { useInfrastructureItemMapping } from '@/features/items/hooks/use-infrastructure-item-mapping';
import { calculateCircuitTotalLoads } from '@/features/items/utils/calculate-circuit-total-loads';
import {
  getNextCircuitNumber,
  sortCircuitsByNumber,
} from '@/features/items/utils/get-next-circuit-number';
import { SelectPowerTypeModal } from '@/features/power-type/components/select-power-type-modal';
import { PowerTypeEntity } from '@/types/api';

import { ItemFormData } from './types';

interface ItemCircuitDataWithIndex extends ItemCircuitData {
  index: number;
}

interface CircuitForm {
  circuits: ItemCircuitDataWithIndex[];
}

const circuitFormShape = z.object({
  circuits: z.array(itemCircuitDataDto.extend(z.object({ index: z.number() }).shape)),
});

const createItemCircuitDataWithIndex = (
  circuit: Partial<ItemCircuitData> | null | undefined,
  index: number,
): ItemCircuitDataWithIndex => {
  return {
    id: circuit?.id ?? undefined,
    name: circuit?.name ?? '',
    label: circuit?.label ?? '',
    phase: circuit?.phase ?? undefined,
    wires: circuit?.wires ?? undefined,
    type: circuit?.type ?? undefined,
    powerTypeId: circuit?.powerTypeId ?? undefined,
    voltage: circuit?.voltage ?? undefined,
    index,
  } as ItemCircuitDataWithIndex;
};

export const ItemCircuitForm = () => {
  const { t } = useTranslation();
  const { setValue, watch, formState } = useFormContext<ItemFormData>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSelectPowerTypeModalOpen, setIsSelectPowerTypeModalOpen] = useState(false);
  const [addCount, setAddCount] = useState(1);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const { orgId, projectId } = useScopeIds('org', 'project');
  const tempPowerTypeForm = useForm<{ tempSelectedPowerType: PowerTypeEntity | null }>({
    defaultValues: { tempSelectedPowerType: null },
  });

  const circuitsForm = useForm<CircuitForm>({
    defaultValues: {
      circuits: [],
    },
    mode: 'onChange',
    resolver: zodResolver(circuitFormShape),
  });
  // Must read isDirty during render, or RHF never subscribes and it stays stale.
  const { isDirty: isCircuitsDirty } = useFormState({ control: circuitsForm.control });

  // Get circuits and infrastructures from form context
  const parentCircuits = watch('circuits');
  const infrastructures = watch('infrastructures');
  const currentItemId = watch('id');
  const childCircuits = circuitsForm.watch('circuits');
  const totalCircuits = parentCircuits.length;

  // Get child item IDs from electricity infrastructure relationships for total load calculation
  const childItemIds = useMemo(() => {
    return [
      ...new Set(
        infrastructures
          .filter(
            (infra) =>
              infra.infrastructureType === 'electricity' &&
              infra.parentItemId === currentItemId &&
              'circuitId' in infra &&
              infra.circuitId,
          )
          .map((infra) => infra.childItemId),
      ),
    ];
  }, [infrastructures, currentItemId]);

  const { getItem } = useInfrastructureItemMapping({
    itemIds: childItemIds,
    enabled: isDialogOpen && childItemIds.length > 0,
  });

  // Calculate total load per circuit
  const circuitTotalLoads = useMemo(() => {
    if (!currentItemId) return {};
    return calculateCircuitTotalLoads({
      infrastructures: infrastructures as {
        parentItemId: string;
        childItemId: string;
        infrastructureType: string;
        circuitId?: string;
      }[],
      currentItemId,
      getItem,
    });
  }, [infrastructures, currentItemId, getItem]);

  const resetCircuitsForm = useCallback(() => {
    const newCircuits = parentCircuits.map((circuit, index) => {
      return createItemCircuitDataWithIndex(circuit, index);
    });

    circuitsForm.reset(
      {
        circuits: newCircuits,
      },
      {
        keepDefaultValues: false,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCircuits, circuitsForm.reset]);

  useEffect(() => {
    resetCircuitsForm();
  }, [resetCircuitsForm]);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCancel = () => {
    resetCircuitsForm();
    setIsDialogOpen(false);
  };

  const handleDialogClose = (_event: unknown, reason: string) => {
    if ((reason === 'backdropClick' || reason === 'escapeKeyDown') && isCircuitsDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    handleCancel();
  };

  const handleCancelDiscard = () => {
    setDiscardConfirmOpen(false);
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    handleCancel();
  };

  const handleSave = () => {
    const circuits = circuitsForm.getValues('circuits').map((circuit) => ({
      ...circuit,
      label: circuit.label?.trim() ? circuit.label : circuit.name,
    }));
    setValue('circuits', circuits, { shouldDirty: true });
    // The form context will automatically update when we close
    setIsDialogOpen(false);
  };

  const handleAddCircuits = () => {
    setEditIndex(null);
    tempPowerTypeForm.setValue('tempSelectedPowerType', null);
    setIsSelectPowerTypeModalOpen(true);
  };

  const handleDeleteCircuit = useCallback(
    (index: number) => {
      const currentCircuits = circuitsForm.getValues('circuits');
      const updatedCircuits = currentCircuits
        .filter((_, i) => i !== index)
        .map((circuit, index) => ({ ...circuit, index }));
      circuitsForm.setValue('circuits', updatedCircuits, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [circuitsForm.getValues, circuitsForm.setValue],
  );

  const handleOnPowerTypeSelected = useCallback(
    (powerType: PowerTypeEntity | PowerTypeEntity[] | null) => {
      const powerTypeToUse = Array.isArray(powerType) ? powerType[0] : powerType;
      if (!powerTypeToUse) return;
      const allCircuits = circuitsForm.getValues('circuits');

      if (editIndex !== null) {
        // Editing an existing row
        const currentCircuit = circuitsForm.getValues(`circuits.${editIndex}`);
        const isNewCircuit = !currentCircuit.id;
        const updatedCircuit = {
          ...currentCircuit,
          powerTypeId: powerTypeToUse.id,
          type: powerTypeToUse.type,
          voltage: powerTypeToUse.voltage,
          wires: powerTypeToUse.wires,
          phase: powerTypeToUse.phase,
        };

        if (isNewCircuit) {
          updatedCircuit.name = getNextCircuitNumber(allCircuits, powerTypeToUse.voltage);
        }

        circuitsForm.setValue(`circuits.${editIndex}`, updatedCircuit, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        // Adding new rows via +Add button
        const newCircuits = [...allCircuits];
        for (let i = 0; i < addCount; i++) {
          const newIndex = newCircuits.length;
          const name = getNextCircuitNumber(newCircuits, powerTypeToUse.voltage);
          const newCircuit = createItemCircuitDataWithIndex(
            {
              powerTypeId: powerTypeToUse.id,
              type: powerTypeToUse.type,
              voltage: powerTypeToUse.voltage,
              wires: powerTypeToUse.wires,
              phase: powerTypeToUse.phase,
              name,
            },
            newIndex,
          );
          // Push to local array so next iteration gets correct next number
          newCircuits.push(newCircuit);
        }
        circuitsForm.setValue('circuits', newCircuits, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setAddCount(1);
      }
    },
    [circuitsForm, editIndex, addCount],
  );

  /**
   * Right now there is a bug where the form creates a row (after being reset). I believe this is because of trailing components
   * Updating the form value. So to prevent it from adding a row, we just filter out any rows created by the form, which is identified
   * by the index being undefined.
   * !IMPORTANT: If the row is undefined, the page will crash.
   */
  const filteredRows = useMemo(
    () => sortCircuitsByNumber(childCircuits.filter((row) => 'index' in row)),
    [childCircuits],
  );

  const columns = useMemo(
    (): GridColDef<ItemCircuitDataWithIndex>[] => [
      {
        field: 'number',
        headerName: t('itemTemplates.detail.circuitNumber', '番号'),
        flex: 1,
        editable: false,
        renderCell: ({ row }) => {
          return (
            <TextFieldElement
              size="small"
              disabled={formState.disabled}
              name={`circuits.${row.index}.name`}
              onKeyDown={(event) => {
                // This prevents the issue where DataGrid auto scrolls to bottom if we press any key while the text field is focused
                // for example pressing space will scroll to bottom instead of adding a space to the text field
                event.stopPropagation();
              }}
            />
          );
        },
      },
      {
        field: 'label',
        headerName: t('itemTemplates.detail.circuitLabel', 'ラベル'),
        flex: 1,
        editable: false,
        renderCell: ({ row }) => {
          return (
            <TextFieldElement
              size="small"
              type="text"
              disabled={formState.disabled}
              name={`circuits.${row.index}.label`}
              onKeyDown={(event) => {
                // This prevents the issue where DataGrid auto scrolls to bottom if we press any key while the text field is focused
                // for example pressing space will scroll to bottom instead of adding a space to the text field
                event.stopPropagation();
              }}
            />
          );
        },
      },
      {
        field: 'circuit',
        headerName: t('itemTemplates.detail.typeOfPower', '電力種別'),
        flex: 1,
        editable: false,
        renderCell: ({ row }) => {
          return (
            <Typography variant="body2">{stringifyPowerType(childCircuits[row.index])}</Typography>
          );
        },
      },
      {
        field: 'totalLoad',
        headerName: t('itemTemplates.detail.circuitTotalLoad', '総負荷'),
        flex: 1,
        editable: false,
        renderCell: ({ row }) => {
          const totalLoad = row.id ? circuitTotalLoads[row.id] : undefined;

          if (!totalLoad) {
            return null;
          }

          if (totalLoad.hasMixedUnits) {
            return (
              <Tooltip
                title={t(
                  'itemTemplates.detail.circuitTotalLoadMixedUnitsTooltip',
                  'Child item loads use different units. Total load is not calculated.',
                )}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <WarningAmberIcon color="warning" fontSize="small" />
                  <Typography variant="body2" color="warning.main">
                    {t('itemTemplates.detail.circuitTotalLoadMixedUnits', 'Mixed units')}
                  </Typography>
                </Stack>
              </Tooltip>
            );
          }

          return (
            <Typography variant="body2">
              {`${totalLoad.load} ${totalLoad.unit ?? ''}`.trim()}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: t('common.actions', '操作'),
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          return (
            <>
              <IconButton
                size="small"
                onClick={() => {
                  setEditIndex(row.index);
                  tempPowerTypeForm.setValue('tempSelectedPowerType', null);
                  setIsSelectPowerTypeModalOpen(true);
                }}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  handleDeleteCircuit(row.index);
                }}
                disabled={formState.disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          );
        },
      },
    ],
    [circuitTotalLoads, formState.disabled, handleDeleteCircuit, t, tempPowerTypeForm],
  );

  return (
    <>
      {/* Display: Circuits chip with edit button */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2">{t('itemTemplates.detail.circuit')}</Typography>
        <Chip
          label={t('itemTemplates.detail.circuitsCount', { count: totalCircuits })}
          size="small"
          variant="filled"
          color="primary"
        />
        <Tooltip title={t('common.edit')}>
          <IconButton size="small" onClick={handleOpenDialog} color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Edit Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
      >
        <DialogTitle>{t('itemTemplates.detail.editCircuits', '回路の編集')}</DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{ mt: 1, height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {/* Add circuits section */}
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                type="number"
                label={t('itemTemplates.detail.addCircuits', '追加する回路数')}
                disabled={formState.disabled}
                value={addCount}
                onChange={(e) => {
                  setAddCount(Math.max(1, parseInt(e.target.value) || 1));
                }}
                slotProps={{
                  htmlInput: {
                    min: 1,
                  },
                }}
                size="small"
                sx={{ width: 150 }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddCircuits}
                disabled={formState.disabled}
                size="small"
              >
                {t('common.add', '追加')}
              </Button>
            </Stack>

            {/* Circuits DataGrid */}
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <FormProvider {...circuitsForm}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  rowSelection={false}
                  getRowId={(row) => row.index}
                  disableColumnSelector
                  disableRowSelectionOnClick
                  disableMultipleRowSelection
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 10 },
                    },
                  }}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      py: 1,
                    },
                  }}
                />
              </FormProvider>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!circuitsForm.formState.isValid || formState.disabled}
          >
            {t('common.select')}
          </Button>
        </DialogActions>
      </Dialog>

      <FormProvider {...tempPowerTypeForm}>
        <SelectPowerTypeModal
          name="tempSelectedPowerType"
          orgId={orgId}
          projectId={projectId}
          open={isSelectPowerTypeModalOpen}
          multiValue={false}
          onSelect={handleOnPowerTypeSelected}
          onCancel={() => {
            setEditIndex(null);
          }}
          onClose={() => {
            setIsSelectPowerTypeModalOpen(false);
          }}
        />
      </FormProvider>

      <ConfirmDialog
        open={discardConfirmOpen}
        onClose={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        title={t('items.edit.discardTitle', 'Discard unsaved changes?')}
        message={t(
          'items.edit.discardMessage',
          'You have unsaved changes. Do you want to discard them and close?',
        )}
        confirmText={t('items.edit.discardConfirm', 'Discard')}
        cancelText={t('items.edit.discardCancel', 'Keep editing')}
      />
    </>
  );
};
